import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FaPlus, FaMinus } from "../../../assets/icons/vander";
import { useDoctorsStore, useSelectFormStore, useForm } from "../../../hooks";
import { replaceNullToEmpty } from "../../../helpers";
import {
  InputForm,
  SelectForm,
  LoadingData,
  ErrorMessage,
  CancelButton,
  MainButton,
} from "../../ui";
import { useFormMessages } from "../../hooks";

const formValidations = {
  document: [
    (value) => value.length > 7,
    "El documento debe ser mayor a 7 caracteres",
  ],
  documentTypeId: [
    (value) => value.length > 0,
    "Selecciona un tipo de documento",
  ],
  firstName: [(value) => value.length > 0, "Los nombres no pueden ser vacios"],
  lastName: [(value) => value.length > 0, "Los apellidos no pueden ser vacios"],
  email: [(value) => value.includes("@"), "El email debe contener una @"],
  phoneNumber: [
    (value) => value.length > 9,
    "El numero de celular debe contener más de 9 digitos",
  ],
  countryPhoneId: [
    (value) => value.length > 0,
    "Selecciona un pais del celular",
  ],
  country: [(value) => value.length > 0, "El país no puede ser vacio"],
  department: [
    (value) => value.length > 0,
    "El departamento no puede ser vacio",
  ],
  city: [(value) => value.length > 0, "La ciudad no puede ser vacia"],
  address: [(value) => value.length > 0, "La dirección no puede ser vacia"],
  genderId: [(value) => value.length > 0, "Selecciona un género"],
  specialtyId: [(value) => value.length > 0, "Selecciona una especialidad"],
  medicalRegistry: [
    (value) => value.length > 0,
    "El regitro médico no puede ser vacio",
  ],
};

const initialFormData = () => {
  return Object.keys(formValidations).reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});
};

export const ModalEditDoctor = ({ editDoctor, setEditDoctor, doctor }) => {
  // Datos de hooks ----------------------------------------//
  const {
    // Props
    message,
    isLoading,

    // Methods
    startUpdateDoctor,
  } = useDoctorsStore();

  const { documentTypes, countryPhones, genders, headquarters, specialties } =
    useSelectFormStore([
      "documentTypes",
      "countryPhones",
      "genders",
      "headquarters",
      "specialties",
    ]);

  // States ------------------------------------------------//
  const [validateForm, setValidateForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [headquartersForm, setHeadquartersForm] = useState([]);

  // Formulario --------------------------------------------//
  const { formState, formValidation, onInputChange, isFormValid } = useForm(
    formData,
    formValidations
  );

  const {
    setFormSubmitted,
    showErrorMessage,
    showSuccessMessage,
    messageText,
  } = useFormMessages({
    message,
    setValidateForm,
  });

  const {
    document,
    documentTypeId,
    firstName,
    lastName,
    email,
    phoneNumber,
    countryPhoneId,
    country,
    department,
    city,
    address,
    genderId,
    specialtyId,
    medicalRegistry,
  } = formState;

  const {
    documentValid,
    documentTypeIdValid,
    firstNameValid,
    lastNameValid,
    emailValid,
    phoneNumberValid,
    countryPhoneIdValid,
    countryValid,
    departmentValid,
    cityValid,
    addressValid,
    genderIdValid,
    specialtyIdValid,
    medicalRegistryValid,
  } = formValidation;

  // Funciones ---------------------------------------------//

  const handleChange = (index, value) => {
    setHeadquartersForm((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              headquarterId: value,
              headquarterIdValid: value ? null : "Selecciona una Sede - ips",
            }
          : item
      )
    );
  };

  const handleAddField = () => {
    setHeadquartersForm((prev) => {
      // Verifica si el último campo tiene un `headquarterId` seleccionado
      const lastField = prev[prev.length - 1];

      // Si el último campo no tiene un `headquarterId`, muestra el error
      if (!lastField.headquarterId) {
        return prev.map((item, index) =>
          index === prev.length - 1 ? { ...item, isError: true } : item
        );
      }

      // Si tiene un `headquarterId`, marca el último campo como `disabled`
      const updatedList = prev.map((item, index) =>
        index === prev.length - 1 ? { ...item, disabled: true } : item
      );

      // Calcula las headquarters disponibles para el nuevo campo
      const availableHeadquarters = lastField.availableHeadquarters.filter(
        (item) => item.id !== lastField.headquarterId
      );

      if (availableHeadquarters.length === 0) return prev;

      // Añade un nuevo campo vacío con las IPS actualizadas
      return [
        ...updatedList,
        {
          headquarterId: "",
          headquarterIdValid: "Seleccione una Sede - ips",
          isError: false,
          availableHeadquarters,
          disabled: false,
        },
      ];
    });
  };

  const handleRemoveField = () => {
    setHeadquartersForm((prev) => {
      if (prev.length > 1) {
        // Crea una nueva lista eliminando el último elemento
        const updatedList = prev.slice(0, -1);
        // Asegúrate de que el último elemento de la lista actualizada tenga disabled: false
        updatedList[updatedList.length - 1] = {
          ...updatedList[updatedList.length - 1],
          disabled: false,
        };
        return updatedList;
      }
      return prev; // Si solo queda uno, no hace nada
    });
  };

  const onUpdateDoctor = () => {
    setValidateForm(true);

    setHeadquartersForm((prev) => {
      // Verifica si el último campo tiene un `headquarterId` seleccionado
      const lastField = prev[prev.length - 1];

      // Si el último campo no tiene un `headquarterId`, muestra el error
      if (!lastField.headquarterId) {
        return prev.map((item, index) =>
          index === prev.length - 1 ? { ...item, isError: true } : item
        );
      }
      return prev;
    });

    // Extraer solo los `headquarterId` de la lista
    const selectedHeadquartersId = headquartersForm.map(
      (item) => item.headquarterId
    );

    if (
      isFormValid &&
      headquartersForm[headquartersForm.length - 1].headquarterId
    ) {
      setFormSubmitted(true);
      startUpdateDoctor(formState, selectedHeadquartersId);
    }
  };

  const startHeadquartersForm = () => {
    setHeadquartersForm([
      {
        headquarterId: "",
        headquarterIdValid: "Selecciona una Sede - ips",
        isError: false,
        availableHeadquarters: headquarters,
        disabled: false,
      },
    ]);
  };

  // Efectos -----------------------------------------------//
  useEffect(() => {
    if (doctor) {
      const { headquarters: headquartersDoctor, ...doctorWithoutHeadquarters } =
        doctor;

      const doctorWithoutNulls = replaceNullToEmpty(doctorWithoutHeadquarters);
      setFormData({
        ...doctorWithoutNulls,
      });

      if (headquartersDoctor.length > 0) {
        const updatedHeadquarters = [];
        let availableHeadquarters = [...headquarters];

        for (let i = 0; i < headquartersDoctor.length; i++) {
          updatedHeadquarters.push({
            headquarterId: headquartersDoctor[i].id,
            headquarterIdValid: "Selecciona una Sede - ips",
            isError: false,
            availableHeadquarters: [...availableHeadquarters],
            disabled: i !== headquartersDoctor.length - 1,
          });

          availableHeadquarters = availableHeadquarters.filter(
            (headquarter) => headquarter.id !== headquartersDoctor[i].id
          );
        }

        return setHeadquartersForm(updatedHeadquarters);
      }

      return startHeadquartersForm();
    }
  }, [doctor]);

  useEffect(() => {
    if (!editDoctor) {
      setFormData(initialFormData);
      setValidateForm(false);
      setFormSubmitted(false);
    }
  }, [editDoctor]);

  useEffect(() => {
    if (showSuccessMessage) {
      setEditDoctor(false);
    }
  }, [showSuccessMessage]);

  return (
    <Modal
      show={editDoctor}
      onHide={() => setEditDoctor(false)}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <h5 className="modal-title" id="exampleModalLabel1">
          Editar doctor
        </h5>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          {/* Formulario estatico */}
          <>
            <div className="col-md-6">
              <InputForm
                label={"Documento"}
                type={"text"}
                placeholder={"Documento"}
                validation={documentValid}
                submit={validateForm}
                name={"document"}
                value={document}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <SelectForm
                label={"Tipo de documento"}
                options={documentTypes}
                labelKey={"description"}
                validation={documentTypeIdValid}
                submit={validateForm}
                name={"documentTypeId"}
                value={documentTypeId}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <InputForm
                label={"Nombres"}
                type={"text"}
                placeholder={"Nombres"}
                validation={firstNameValid}
                submit={validateForm}
                name={"firstName"}
                value={firstName}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <InputForm
                label={"Apellidos"}
                type={"text"}
                placeholder={"Apellidos"}
                validation={lastNameValid}
                submit={validateForm}
                name={"lastName"}
                value={lastName}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <InputForm
                label={"Email"}
                type={"text"}
                placeholder={"Email"}
                validation={emailValid}
                submit={validateForm}
                name={"email"}
                value={email}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <InputForm
                label={"No. celular"}
                type={"text"}
                placeholder={"No. celular"}
                validation={phoneNumberValid}
                submit={validateForm}
                name={"phoneNumber"}
                value={phoneNumber}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <SelectForm
                label={"País del celular"}
                options={countryPhones}
                labelKey={"name"}
                validation={countryPhoneIdValid}
                submit={validateForm}
                name={"countryPhoneId"}
                value={countryPhoneId}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <hr />
            </div>
            <div className="col-md-12">
              <label className="form-label">Dirección</label>
            </div>
            <div className="col-md-6">
              <InputForm
                label={"Pais"}
                type={"text"}
                placeholder={"Pais"}
                validation={countryValid}
                submit={validateForm}
                name={"country"}
                value={country}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <InputForm
                label={"Departamento"}
                type={"text"}
                placeholder={"Departamento"}
                validation={departmentValid}
                submit={validateForm}
                name={"department"}
                value={department}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <InputForm
                label={"Ciudad"}
                type={"text"}
                placeholder={"Ciudad"}
                validation={cityValid}
                submit={validateForm}
                name={"city"}
                value={city}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <InputForm
                label={"Dirección"}
                type={"text"}
                placeholder={"Dirección"}
                validation={addressValid}
                submit={validateForm}
                name={"address"}
                value={address}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <hr />
            </div>
            <div className="col-md-6">
              <SelectForm
                label={"Genero"}
                options={genders}
                labelKey={"name"}
                validation={genderIdValid}
                submit={validateForm}
                name={"genderId"}
                value={genderId}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <SelectForm
                label={"Especialidad"}
                options={specialties}
                labelKey={"name"}
                validation={specialtyIdValid}
                submit={validateForm}
                name={"specialtyId"}
                value={specialtyId}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-6">
              <InputForm
                label={"Registro médico"}
                type={"text"}
                placeholder={"Registro médico"}
                validation={medicalRegistryValid}
                submit={validateForm}
                name={"medicalRegistry"}
                value={medicalRegistry}
                onInputChange={onInputChange}
              />
            </div>
            <div className="col-md-12">
              <hr />
            </div>
          </>

          {/* Formulario dinamico */}
          <>
            <div className="col-md-12">
              <h6 className="">Asignar Sede - ips</h6>
            </div>
            {headquartersForm.map((item, index) => (
              <div className="col-md-12" key={index}>
                <SelectForm
                  label={`${index + 1}. Sede - ips`}
                  options={item.availableHeadquarters}
                  labelKey={"name"}
                  validation={item.headquarterIdValid}
                  submit={item.isError}
                  name={`headquarterId-${index}`}
                  value={item.headquarterId}
                  onInputChange={(e) => handleChange(index, e.target.value)}
                  disabled={item.disabled}
                />
              </div>
            ))}
            <div className="col-md-6 d-flex align-items-center">
              <button
                type="button"
                className="btn btn-icon btn-soft-blue"
                onClick={() => handleAddField()}
                disabled={headquartersForm.length >= headquarters.length}
              >
                <FaPlus className="icons" />
              </button>
              <button
                type="button"
                className="btn btn-icon btn-soft-blue ms-2"
                onClick={() => handleRemoveField()}
                disabled={headquartersForm.length <= 1}
              >
                <FaMinus className="icons" />
              </button>
            </div>
          </>

          {/* Mensajes */}
          <>
            {isLoading && (
              <div className="col-md-12">
                <LoadingData />
              </div>
            )}
            {showErrorMessage && (
              <div className="col-md-12">
                <ErrorMessage>{messageText}</ErrorMessage>
              </div>
            )}
          </>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton
          type={"button"}
          onClick={() => setEditDoctor(false)}
          disabled={isLoading}
        >
          Cancelar
        </CancelButton>
        <MainButton
          type={"button"}
          onClick={() => onUpdateDoctor()}
          disabled={isLoading}
        >
          Guardar cambios
        </MainButton>
      </Modal.Footer>
    </Modal>
  );
};
